import React, { useState, useEffect } from "react";
import { nanoid } from "nanoid";
import { useTheme } from "../config/ThemeContext";
import ScoutingHelpModal from "./ScoutingHelp";
import logger from "../utils/logger";

function ScoutingBar({ matchId, setId, elapsedTime, socket, inputRef, onNewEntry }) {
  const [code, setCode] = useState("");
  const [showHelp, setShowHelp] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [validationWarning, setValidationWarning] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [commandHistory, setCommandHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const theme = useTheme();

 const startNextRally = () => {
   socket.emit("nextRally", { matchId });
   setValidationError("");
   setValidationWarning("");
   setInfoMessage("✅ New rally started. Enter serve.");
   setTimeout(() => setInfoMessage(""), 3000);
   inputRef.current?.focus();
 };


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;

    const actionCode = code.trim();
    const uuid = nanoid(10);

    // Add to command history
    setCommandHistory((prev) => [...prev, actionCode]);
    setHistoryIndex(-1);

    try {
      const timestamp = !isNaN(elapsedTime)
        ? new Date(elapsedTime * 1000).toISOString().substr(14, 5)
        : "00:00";

      const scoutingEntry = { timestamp, code: actionCode, uuid };
      logger.trace("📤 [TRACE] ScoutingBar: Emitting scoutingUpdate event:", { matchId, scoutingEntry });

      const optimisticEntry = {
        uuid,
        actionCode,
        matchTimeElapsed: Number(elapsedTime) || 0,
        status: "pending",
      };
	  
	  logger.trace("📤 [TRACE] ScoutingBar: Triggering onNewEntry (optimistic update)");
      if (onNewEntry) {
	    try {
          onNewEntry(optimisticEntry);
		  logger.trace("✅ [TRACE] ScoutingBar: onNewEntry completed");
		} catch (e) {
		  logger.trace("❌ [TRACE] ScoutingBar: onNewEntry failed!", e);
		  throw e;
		}
	  }

      if (socket) {
	    logger.trace("📤 [TRACE] ScoutingBar: Calling socket.emit for scoutingUpdate. Socket id:", socket.id);
        socket.emit("scoutingUpdate", { matchId, setId, scoutingEntry });
		logger.trace("✅ [TRACE] ScoutingBar: socket.emit completed");
      } else {
        logger.trace("⚠️ [TRACE] ScoutingBar: socket is null or undefined, cannot emit.");
      }

      setCode(""); // Reset input
      setIsTyping(false);
    } catch (error) {
      console.error("❌ Error processing action code:", error);
    }
  };

  useEffect(() => {
    if (inputRef?.current) {
      inputRef.current.focus();
    }
  }, [inputRef]);

  useEffect(() => {
    if (!socket) return;

    socket.on("scoutingError", (data) => {
      if (data.matchId === matchId) {
        setValidationError(data.error);
        setInfoMessage("");
      }
    });

    socket.on("scoutingWarning", (data) => {
      if (data.matchId === matchId) {
        setValidationWarning(data.warnings?.[0]?.message || "Warning");
        setInfoMessage("");
      }
    });

    socket.on("clearScoutingError", () => {
      setValidationError("");
      setValidationWarning("");
      setInfoMessage("");
    });

    return () => {
      socket.off("scoutingError");
      socket.off("scoutingWarning");
      socket.off("clearScoutingError");
    };
  }, [socket, matchId]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === "Enter") {
        e.preventDefault();
		startNextRally();
      }

      // Command history navigation
      if (e.key === "ArrowUp" && !e.shiftKey) {
        e.preventDefault();
        if (historyIndex < commandHistory.length - 1) {
          const newIndex = historyIndex + 1;
          setHistoryIndex(newIndex);
          setCode(commandHistory[commandHistory.length - 1 - newIndex]);
        }
      }

      if (e.key === "ArrowDown" && !e.shiftKey) {
        e.preventDefault();
        if (historyIndex > 0) {
          const newIndex = historyIndex - 1;
          setHistoryIndex(newIndex);
          setCode(commandHistory[commandHistory.length - 1 - newIndex]);
        } else if (historyIndex === 0) {
          setHistoryIndex(-1);
          setCode("");
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [matchId, socket, inputRef, commandHistory, historyIndex]);

  const handleInputChange = (e) => {
    setValidationError("");
    setValidationWarning("");
    setInfoMessage("");
    setCode(e.target.value);
    setIsTyping(e.target.value.length > 0);
    setHistoryIndex(-1);
  };

  const getInputClassName = () => {
    let baseClasses = `
      w-full px-3 py-2 text-lg font-mono
      bg-white border-2 rounded-lg
      focus:outline-none focus:ring-4
      transition-all duration-200
      placeholder-gray-400
    `;

    if (validationError) {
      baseClasses += " border-red-500 focus:ring-red-300 focus:border-red-500";
    } else if (validationWarning) {
      baseClasses += " border-yellow-500 focus:ring-yellow-300 focus:border-yellow-500";
    } else if (isTyping) {
      baseClasses += " border-green-500 focus:ring-green-300 focus:border-green-500";
    } else {
      baseClasses += " border-gray-300 focus:ring-blue-300 focus:border-blue-500";
    }

    return baseClasses;
  };

  return (
    <div className="w-full">
      {/* Scouting Input Section */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        {/* Input Area */}
        <div className="p-3">
          <form onSubmit={handleSubmit} className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowHelp(true)}
              className={`
                px-3 py-2 rounded-lg
                ${theme.buttons.tertiary}
                font-semibold text-sm
                transition-all duration-200
                focus:outline-none focus:ring-4 focus:ring-blue-300
                active:scale-95
              `}
              aria-label="Help"
              title="Help on scouting codes"
            >
              ?
            </button>

            {/* Input Field */}
            <input
              ref={inputRef}
              type="text"
              placeholder="Enter code..."
              value={code}
              autoComplete="off"
              onChange={handleInputChange}
              className={getInputClassName()}
              spellCheck="false"
            />

            {/* Action Buttons */}
            <div className="flex gap-1">
              <button
                type="submit"
                className={`
                  px-4 py-2 rounded-lg
                  ${theme.buttons.primary}
                  font-semibold text-sm
                  transition-all duration-200
                  focus:outline-none focus:ring-4 focus:ring-blue-300
                  active:scale-95
                `}
              >
                Enter
              </button>

              <button
                type="button"
                title="Ctrl + Enter to start next rally"
                onClick={startNextRally}
                className={`
                  px-3 py-2 rounded-lg
                  ${theme.buttons.secondary}
                  font-semibold text-sm
                  transition-all duration-200
                  focus:outline-none focus:ring-4 focus:ring-blue-300
                  active:scale-95
                `}
              >
                Next Rally
              </button>
            </div>
          </form>

          {/* Status Messages */}
          <div className="mt-2 space-y-1">
            {validationError && (
              <div className="
                flex items-center gap-2 px-2 py-1
                bg-red-50 border border-red-200
                text-red-700 rounded text-sm
              ">
                <span className="text-red-500">❌</span>
                <span className="font-medium">{validationError}</span>
              </div>
            )}
            {validationWarning && (
              <div className="
                flex items-center gap-2 px-2 py-1
                bg-yellow-50 border border-yellow-200
                text-yellow-700 rounded text-sm
              ">
                <span className="text-yellow-500">⚠️</span>
                <span className="font-medium">{validationWarning}</span>
              </div>
            )}
            {infoMessage && (
              <div className="
                flex items-center gap-2 px-2 py-1
                bg-blue-50 border border-blue-200
                text-blue-700 rounded text-sm
              ">
                <span className="text-blue-500">ℹ️</span>
                <span className="font-medium">{infoMessage}</span>
              </div>
            )}
          </div>

          {/* Recent Codes */}
          {commandHistory.length > 0 && (
            <div className="mt-2 pt-2 border-t border-gray-100">
              <div className="text-gray-500 text-xs mb-1">Recent codes:</div>
              <div className="flex flex-wrap gap-1">
                {commandHistory.slice(-4).reverse().map((cmd, index) => (
                  <button
                    key={index}
                    onClick={() => setCode(cmd)}
                    className="
                      px-2 py-1 text-xs
                      bg-gray-100 text-gray-700
                      rounded border border-gray-200
                      hover:bg-gray-200 hover:border-gray-300
                      transition-all duration-200
                      font-mono
                    "
                  >
                    {cmd}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <ScoutingHelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} />
    </div>
  );
}

export default ScoutingBar;
